import type { SportKind } from '@geni/contracts/sport';

/**
 * The collapsed launcher's mini-stage: which sport, which action, how long to
 * rest afterwards.
 *
 * Pure data. Nothing here draws anything or reads a clock — `render.ts` turns a
 * `Teaser` into shapes and `custom-element.ts` decides when to ask for the next
 * one, which is what makes "does it ever repeat itself twice in a row" and
 * "does it rest between actions" assertions on values rather than things
 * somebody has to sit and watch.
 *
 * **On how this is drawn, because the requirement named some libraries.** The
 * stage is SVG geometry animated by CSS keyframes, and that is not a
 * preference. `ElementSpec` has no field that can carry markup — the property
 * that stops a language model's reply becoming HTML — so a sprite sheet, a
 * Lottie player or any renderer that takes a document and parses it would mean
 * adding one. And this bundle has twice regressed by an order of magnitude from
 * a single careless import; Lottie's runtime alone is larger than the entire
 * widget. Shapes and keyframes cost bytes measured in single kilobytes, need no
 * asset host, and cannot introduce a parser.
 *
 * **On lazy loading.** There is nothing to fetch, so there is no loader to
 * write and pretending otherwise would be theatre. What is actually deferred is
 * the work: only the current sport's stage is ever built, only while the
 * launcher is on screen, and the timer that drives it does not run once the
 * panel is open. See `custom-element.ts`.
 */

/** The sports with a stage drawn for them. */
export const TEASER_SPORTS = [
  'SOCCER',
  'AMERICAN_FOOTBALL',
  'BASKETBALL',
  'TENNIS',
  'GOLF',
  'ESPORTS',
] as const;
export type TeaserSport = (typeof TEASER_SPORTS)[number];

/**
 * Three actions each, and they are genuinely different shots rather than the
 * same arc at three speeds — a corner from the left and a corner from the right
 * start on opposite sides, a dunk barely leaves the hand, a lob goes twice as
 * high as a cross-court drive. The geometry for each lives in the stylesheet as
 * four custom properties; see `geni-stage--*`.
 */
export const TEASER_ACTIONS = {
  SOCCER: ['left-corner', 'right-corner', 'dribble'],
  // A gridiron throw is the one arc here that is thrown rather than struck:
  // a bomb hangs, a slant is nearly flat, and a snap barely travels.
  AMERICAN_FOOTBALL: ['deep-bomb', 'slant', 'snap'],
  BASKETBALL: ['three-pointer', 'slam-dunk', 'bank-shot'],
  TENNIS: ['cross-court', 'smash', 'lob'],
  GOLF: ['drive', 'chip', 'putt'],
  ESPORTS: ['flick', 'spray', 'clutch'],
} as const satisfies Record<TeaserSport, readonly [string, string, string]>;

export type TeaserAction = (typeof TEASER_ACTIONS)[TeaserSport][number];

export interface Teaser {
  sport: TeaserSport;
  action: string;
  /** How long the stage rests after this action before the next one. */
  pauseMs: number;
}

/**
 * How long the character stands still between actions.
 *
 * Two to three seconds, and the range matters more than the numbers. A stage
 * that fires every half second reads as a loading spinner and stops being
 * looked at; one that pauses for a beat reads as somebody warming up. Varying
 * the pause is what stops it reading as a machine doing the same thing on a
 * metronome — the eye is very good at spotting a fixed interval.
 */
export const MIN_PAUSE_MS = 2000;
export const MAX_PAUSE_MS = 3000;

/** How long one action takes to play out. Matches the stylesheet's duration. */
export const ACTION_MS = 1400;

/**
 * Which stage to draw for the sport the operator declared.
 *
 * `OTHER` and `null` both return null, and the launcher then draws the mark it
 * always drew. An operator trading darts gets the plain pill rather than a
 * football stage, on the same terms as the starter prompts: a wrong picture is
 * worse than no picture.
 */
export function teaserSportFor(sport: SportKind | null | undefined): TeaserSport | null {
  if (sport === null || sport === undefined) return null;
  return (TEASER_SPORTS as readonly string[]).includes(sport) ? (sport as TeaserSport) : null;
}

/**
 * The next action, and how long to rest after it.
 *
 * `random` is passed in rather than reached for, so a test can pin the whole
 * sequence. Anything outside 0–1 or not a number is clamped rather than trusted
 * — this decides an array index, and an out-of-range one would render a stage
 * with no action class and no animation at all.
 */
export function pickTeaser(
  sport: TeaserSport,
  random: () => number,
  /**
   * What just played.
   *
   * Excluded from the draw, because a person does not take the identical shot
   * twice in a row and a stage that does looks broken rather than random. With
   * three actions this still leaves a real choice; it is not a rotation.
   */
  previous?: Teaser | null,
): Teaser {
  const actions = TEASER_ACTIONS[sport] as readonly string[];
  const repeatable =
    previous?.sport === sport
      ? actions.filter((action) => action !== previous.action)
      : actions;

  // Never empty: `actions` has three entries and at most one is excluded.
  const choices = repeatable.length > 0 ? repeatable : actions;

  return {
    sport,
    action: choices[Math.min(choices.length - 1, Math.floor(unit(random()) * choices.length))]!,
    pauseMs: Math.round(MIN_PAUSE_MS + unit(random()) * (MAX_PAUSE_MS - MIN_PAUSE_MS)),
  };
}

/** A random source is somebody else's function; treat its output as a claim. */
function unit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(0.999999, Math.max(0, value));
}
